import { Box, Card, Grid, Skeleton, useTheme } from "@mui/material";
import AnnualPerformanceTableSkeleton from "../../components/AnnualPerformanceTableSkeleton";

const MetricCardSkeleton = () => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Skeleton variant="text" width="55%" height={20} animation="wave" />
      <Skeleton variant="text" width="75%" height={32} animation="wave" sx={{ mt: 0.5 }} />
    </Box>
  );
};

const StrategyDetailSkeleton = () => {
  const theme = useTheme();

  return (
    <Grid container spacing={3} sx={{ mt: 1, p: 3 }} alignItems="stretch" direction="row-reverse">
      {/* Right column - sidebar */}
      <Grid size={{ xs: 12, md: 3 }} sx={{ display: "flex", flexDirection: "column" }}>
        {/* SimulationPanel skeleton */}
        <Card
          sx={{
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            borderRadius: 2,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Skeleton variant="text" width="60%" height={28} animation="wave" />
            <Skeleton variant="rectangular" width={60} height={24} animation="wave" sx={{ borderRadius: 1 }} />
          </Box>
          <Skeleton variant="rectangular" width="100%" height={56} animation="wave" sx={{ borderRadius: 1 }} />
          <MetricCardSkeleton />
          <MetricCardSkeleton />
        </Card>

        {/* StrategyOwner skeleton */}
        <Card
          sx={{
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            borderRadius: 2,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            border: `1px solid ${theme.palette.divider}`,
            mt: 2,
          }}
        >
          {/* Owner header */}
          <Box display="flex" alignItems="center" gap={2}>
            <Skeleton variant="circular" width={48} height={48} animation="wave" />
            <Box flex={1}>
              <Skeleton variant="text" width="40%" height={16} animation="wave" />
              <Skeleton variant="text" width="70%" height={24} animation="wave" sx={{ mt: 0.5 }} />
              <Skeleton variant="text" width="55%" height={16} animation="wave" />
            </Box>
          </Box>

          {/* Strategy profile box */}
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.default,
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Skeleton variant="circular" width={18} height={18} animation="wave" />
              <Skeleton variant="text" width="50%" height={20} animation="wave" />
            </Box>
            <Skeleton variant="text" width="100%" animation="wave" />
            <Skeleton variant="text" width="90%" animation="wave" />
            <Skeleton variant="text" width="60%" animation="wave" sx={{ mt: 1 }} />
          </Box>

          {/* Other strategies list */}
          <Box>
            <Box display="flex" justifyContent="space-between" mb={1.5}>
              <Skeleton variant="text" width="45%" height={16} animation="wave" />
              <Skeleton variant="text" width="25%" height={16} animation="wave" />
            </Box>
            {[1, 2, 3].map((i) => (
              <Box
                key={i}
                sx={{
                  p: 1.5,
                  mb: 1,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box flex={1}>
                  <Skeleton variant="text" width="65%" height={20} animation="wave" />
                  <Skeleton variant="text" width="45%" height={16} animation="wave" />
                </Box>
                <Skeleton variant="text" width={40} height={20} animation="wave" sx={{ ml: 1 }} />
              </Box>
            ))}
          </Box>
        </Card>
      </Grid>

      {/* Left column - main content */}
      <Grid size={{ xs: 12, md: 9 }}>
        <Card
          sx={{
            borderRadius: 2,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            border: `1px solid ${theme.palette.divider}`,
            p: 3,
          }}
        >
          {/* StrategyHeader skeleton */}
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2} mb={3}>
            <Box flex={1}>
              <Skeleton variant="text" width="55%" height={48} animation="wave" sx={{ mb: 1 }} />
              <Box display="flex" gap={1} mb={2}>
                <Skeleton variant="rectangular" width={80} height={24} animation="wave" sx={{ borderRadius: 4 }} />
                <Skeleton variant="rectangular" width={70} height={24} animation="wave" sx={{ borderRadius: 4 }} />
                <Skeleton variant="rectangular" width={90} height={24} animation="wave" sx={{ borderRadius: 4 }} />
              </Box>
              <Skeleton variant="text" width="100%" animation="wave" />
              <Skeleton variant="text" width="95%" animation="wave" />
              <Skeleton variant="text" width="80%" animation="wave" />
            </Box>
            <Skeleton variant="rectangular" width={200} height={40} animation="wave" sx={{ borderRadius: 1, flexShrink: 0 }} />
          </Box>

          {/* StrategyMetrics skeleton */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[1, 2, 3, 4].map((i) => (
              <Grid key={i} size={{ xs: 12, sm: 3 }}>
                <MetricCardSkeleton />
              </Grid>
            ))}
          </Grid>

          {/* ChartSection skeleton */}
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={2}>
            <Skeleton variant="rectangular" width={120} height={24} animation="wave" sx={{ borderRadius: 4 }} />
            <Box display="flex" alignItems="center" gap={2}>
              <Skeleton variant="rectangular" width={100} height={32} animation="wave" sx={{ borderRadius: 1 }} />
              <Skeleton variant="rectangular" width={120} height={32} animation="wave" sx={{ borderRadius: 1 }} />
            </Box>
          </Box>
          <Skeleton
            variant="rectangular"
            width="100%"
            height={600}
            animation="wave"
            sx={{ borderRadius: 2 }}
          />
          <Box sx={{ mt: 3 }}>
            <AnnualPerformanceTableSkeleton />
          </Box>
        </Card>
      </Grid>
    </Grid>
  );
};

export default StrategyDetailSkeleton;
